import React, { useState, useEffect, useRef } from "react";
import * as services from "../../../services/saleng";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import { formatNumber } from "../../../utils/formatNumber";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getSaleItemSummary } from "../../../utils/salePricing";

export default function TopMonthlySalesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  const STATUS = "payed";

  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  const fetchRows = async () => {
    setLoading(true);
    try {
      const startDate = new Date(selectedYear, selectedMonth, 1)
        .toISOString()
        .split("T")[0];
      const endDate = new Date(selectedYear, selectedMonth + 1, 0)
        .toISOString()
        .split("T")[0];

      const res = await services.getSaleTransactions({
        status: STATUS,
        startDate,
        endDate,
      });

      const data = res?.data?.data ?? [];

      const grouped = new Map();

      for (const t of data) {
        const cid = t.customerId ?? "unknown";
        const key = String(cid);

        const sumNet =
          t.items?.reduce((s, it) => s + getSaleItemSummary(it).amount, 0) || 0;

        const vatIncluded = t.isVat === true;
        const beforeVat = vatIncluded ? sumNet / 1.07 : sumNet;
        const vat = vatIncluded ? sumNet - beforeVat : 0;

        if (!grouped.has(key)) {
          grouped.set(key, {
            customerId: cid,
            customerCode: t.customer?.code || "",
            customerName: t.customer?.name || t.customerName || "-",
            branchName: t.branch?.name || "-",
            personType: t.customer?.personType || "-",
            weight: 0,
            totalBeforeVat: 0,
            vatAmount: 0,
            grandTotal: 0,
          });
        }

        const g = grouped.get(key);
        g.weight += t.items?.reduce((s, it) => s + (it.netWeight || 0), 0) || 0;
        g.totalBeforeVat += beforeVat;
        g.vatAmount += vat;
        g.grandTotal += sumNet;
      }

      const result = Array.from(grouped.values())
        .sort((a, b) => b.grandTotal - a.grandTotal)
        .slice(0, 10);

      setRows(result);
    } catch (err) {
      console.error("โหลดข้อมูลไม่สำเร็จ", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename = "export.csv") => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExport = () => {
    if (!rows.length) return;
    
    // Transform data for export (cleaner headers)
    const dataset = rows.map((r, index) => ({
      อันดับ: index + 1,
      รหัสลูกค้า: r.customerCode,
      ชื่อลูกค้า: r.customerName,
      สาขา: r.branchName,
      ประเภท: mapPersonType[r.personType] || "-",
      น้ำหนักรวม: Number(r.weight || 0).toFixed(2),
      ยอดก่อนภาษี: Number(r.totalBeforeVat || 0).toFixed(2),
      ภาษี: Number(r.vatAmount || 0).toFixed(2),
      ยอดสุทธิ: Number(r.grandTotal || 0).toFixed(2),
    }));

    exportToCSV(dataset, `10อันดับยอดขายรายบุคคล_${selectedMonth + 1}_${selectedYear + 543}.csv`);
  };

  useEffect(() => {
    fetchRows();
  }, [selectedMonth, selectedYear]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `10อันดับยอดขายรายบุคคล_${selectedMonth + 1}_${selectedYear + 543}`,
  });

  const months = [
    "มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน",
    "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม",
  ];

  const mapPersonType = {
    normal: "บุคคลธรรมดา",
    company: "นิติบุคคล",
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

        {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1 h-8 bg-green-600 rounded-full"></span>
              10 อันดับ ยอดขายรายบุคคลประจำเดือน
            </h1>

            <div className="flex flex-wrap items-center gap-3">
              {/* Month Select */}
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">เดือน</span>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm p-0 pr-8 cursor-pointer"
                >
                  {months.map((m, i) => (
                    <option key={i} value={i}>{m}</option>
                  ))}
                </select>
              </div>

               {/* Year Select */}
               <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">ปี</span>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm p-0 pr-8 cursor-pointer"
                >
                  {[selectedYear - 1, selectedYear, selectedYear + 1].map((y) => (
                    <option key={y} value={y}>{y + 543}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm ml-2"
              >
                <PrintIcon fontSize="small" />
                <span>พิมพ์รายงาน</span>
              </button>

              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white border border-transparent rounded-xl hover:bg-green-700 transition-all font-medium shadow-sm hover:shadow-md ml-2"
              >
                <ContentCopyIcon fontSize="small" />
                <span>Excel</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
             <div className="flex justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
             </div>
        ) : (
            <div ref={printRef} className="print-content">
                 {/* Print Header */}
                 <div className="hidden print:block mb-6 text-center">
                    <h2 className="text-xl font-bold text-gray-800">10 อันดับ ยอดขายรายบุคคลประจำเดือน</h2>
                    <p className="text-gray-600 mt-1">
                        ประจำเดือน {months[selectedMonth]} ปี {selectedYear + 543}
                    </p>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4 text-center rounded-tl-lg">อันดับ</th>
                                    <th className="px-6 py-4">ลูกค้า</th>
                                    <th className="px-6 py-4">สาขา</th>
                                    <th className="px-6 py-4">ประเภทลูกค้า</th>
                                    <th className="px-6 py-4 text-right">น้ำหนัก</th>
                                    <th className="px-6 py-4">หน่วย</th>
                                    <th className="px-6 py-4 text-right">ยอดก่อนรวมภาษี</th>
                                    <th className="px-6 py-4 text-right">ยอดภาษี</th>
                                    <th className="px-6 py-4 text-right rounded-tr-lg">ยอดสุทธิ</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {rows.length > 0 ? (
                                    rows.map((row, idx) => (
                                        <tr key={idx} className="hover:bg-green-50/30 transition-colors">
                                            <td className="px-6 py-4 text-center font-bold text-green-700 bg-green-50/50">{idx + 1}</td>
                                            <td className="px-6 py-4 font-medium text-gray-800">{row.customerCode} {row.customerName}</td>
                                            <td className="px-6 py-4 text-gray-600">{row.branchName}</td>
                                            <td className="px-6 py-4 text-gray-600">{mapPersonType[row.personType] || "-"}</td>
                                            <td className="px-6 py-4 text-right text-gray-600">{formatNumber(row.weight)}</td>
                                            <td className="px-6 py-4 text-gray-600">-</td>
                                            <td className="px-6 py-4 text-right text-gray-600">{formatNumber(row.totalBeforeVat)}</td>
                                            <td className="px-6 py-4 text-right text-gray-600">{formatNumber(row.vatAmount)}</td>
                                            <td className="px-6 py-4 text-right font-medium text-gray-900">{formatNumber(row.grandTotal)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="9" className="px-6 py-8 text-center text-gray-500">
                                            ไม่พบข้อมูล
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
}
